import { YesObject } from '@yes.technology/react-toolkit'
import fetchFilteraction from 'modules/filteraction/fetchFilteraction'
import api from 'utils/api'
import {
  ActivityAutocompleteMultiselection,
  ActivityAutocompleteUniselection
} from 'types/shared'
import { MULTISELECTION_SEPARATOR } from 'utils/constants'
import {
  DEFAULT_VALUE_PAGINATION_LIMIT,
  DefaultValueResolver,
  ResolverContext
} from './helpers'

type AutocompleteActivityModel =
  | ActivityAutocompleteUniselection
  | ActivityAutocompleteMultiselection

function getOptionsFilteractionUuid(
  activityModel: AutocompleteActivityModel
): string | undefined {
  return (
    activityModel.data_sources?.find((ds) => ds.type === 'filteraction')?.config
      ?.filteraction ?? activityModel.information_request_filteraction
  )
}

function hasOptionsFilteractionParams(
  activityModel: AutocompleteActivityModel
): boolean {
  const dataSourceParams = activityModel.data_sources?.find(
    (ds) => ds.type === 'filteraction'
  )?.config?.interpolated_params
  const params =
    dataSourceParams ??
    activityModel.information_request_filteraction_query_params
  return !!params?.length
}

async function resolveAutocompleteActivity(
  activityModel: AutocompleteActivityModel,
  context: ResolverContext,
  isMulti: boolean
) {
  if (activityModel.data_sources?.some((ds) => ds.type === 'activity'))
    return null
  if (hasOptionsFilteractionParams(activityModel)) return null

  const optionsFilteractionUuid = getOptionsFilteractionUuid(activityModel)
  if (!optionsFilteractionUuid) return null

  const [defaultFilteraction, optionsFilteraction] = await Promise.all([
    fetchFilteraction(context.filteractionUuid, false, false),
    fetchFilteraction(optionsFilteractionUuid, false, false)
  ])

  if (!defaultFilteraction || !optionsFilteraction) return null

  const mergedQuery = {
    $and: [
      JSON.parse(defaultFilteraction.query),
      JSON.parse(optionsFilteraction.query)
    ]
  }

  const objectclassUuid = (
    optionsFilteraction.filter_action_objectclass as YesObject[]
  )?.[0]?.uuid
  if (!objectclassUuid) return null

  const result = await api.postObjectsFilter(
    {
      query: mergedQuery,
      objectclass: objectclassUuid,
      orderby: [],
      pagination: { offset: 0, limit: DEFAULT_VALUE_PAGINATION_LIMIT },
      visualization: 'classic-list',
      skip_relations: true,
      skip_metadata: true,
      project_fields: { uuid: 1, des: 1 }
    },
    false,
    false
  )

  const objects = result.objects ?? []
  if (!objects.length) return null

  const selectedObjects = isMulti ? objects : [objects[0]]

  return {
    uuid_activity_model: activityModel.uuid,
    des: selectedObjects.map((obj) => obj.uuid).join(MULTISELECTION_SEPARATOR),
    complement: selectedObjects
      .map((obj) => String(obj.des ?? ''))
      .join(MULTISELECTION_SEPARATOR)
  }
}

export const autocompleteUniResolver: DefaultValueResolver = (
  activityModel,
  context
) =>
  resolveAutocompleteActivity(
    activityModel as AutocompleteActivityModel,
    context,
    false
  )

export const autocompleteMultiResolver: DefaultValueResolver = (
  activityModel,
  context
) =>
  resolveAutocompleteActivity(
    activityModel as AutocompleteActivityModel,
    context,
    true
  )
